import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';

const CancelledBookingRefundRequestList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Sample data for Cancelled Booking Refund Request List
  const [refundRequestData] = useState([
    {
      id: 1,
      bookingId: 'BKG001',
      paidAmount: 500000,
      deductionAmount: 50000,
      payableAmount: 450000,
      paymentDetails: 'Cheque - CHQ123456',
      requestBy: 'Rajesh Kumar',
      reason: 'Financial constraints',
      requestDate: '2024-10-01',
      status: 'Pending',
    },
    {
      id: 2,
      bookingId: 'BKG002',
      paidAmount: 750000,
      deductionAmount: 75000,
      payableAmount: 675000,
      paymentDetails: 'DD - DD789012',
      requestBy: 'Priya Sharma',
      reason: 'Job relocation',
      requestDate: '2024-10-03',
      status: 'Approved',
    },
    {
      id: 3,
      bookingId: 'BKG003',
      paidAmount: 600000,
      deductionAmount: 60000,
      payableAmount: 540000,
      paymentDetails: 'Cash - Receipt #003',
      requestBy: 'Amit Patel',
      reason: 'Medical emergency',
      requestDate: '2024-10-05',
      status: 'Rejected',
    },
    {
      id: 4,
      bookingId: 'BKG004',
      paidAmount: 800000,
      deductionAmount: 80000,
      payableAmount: 720000,
      paymentDetails: 'NEFT - TXN456789',
      requestBy: 'Sunita Verma',
      reason: 'Change in investment plans',
      requestDate: '2024-10-07',
      status: 'Pending',
    },
    {
      id: 5,
      bookingId: 'BKG005',
      paidAmount: 450000,
      deductionAmount: 45000,
      payableAmount: 405000,
      paymentDetails: 'UPI - UPI567890',
      requestBy: 'Vikash Singh',
      reason: 'Property concerns',
      requestDate: '2024-10-08',
      status: 'Processing',
    },
    {
      id: 6,
      bookingId: 'BKG006',
      paidAmount: 650000,
      deductionAmount: 65000,
      payableAmount: 585000,
      paymentDetails: 'Cheque - CHQ654321',
      requestBy: 'Deepika Rao',
      reason: 'Family issues',
      requestDate: '2024-10-09',
      status: 'Approved',
    },
    {
      id: 7,
      bookingId: 'BKG007',
      paidAmount: 920000,
      deductionAmount: 92000,
      payableAmount: 828000,
      paymentDetails: 'DD - DD321654',
      requestBy: 'Karan Gupta',
      reason: 'Business closure',
      requestDate: '2024-10-10',
      status: 'Pending',
    },
    {
      id: 8,
      bookingId: 'BKG008',
      paidAmount: 380000,
      deductionAmount: 38000,
      payableAmount: 342000,
      paymentDetails: 'Cash - Receipt #008',
      requestBy: 'Meera Joshi',
      reason: 'Personal reasons',
      requestDate: '2024-10-11',
      status: 'Processing',
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleApprove = (id) => {
    console.log('Approving refund request with id:', id);
    // Implement approve functionality
  };

  const handleReject = (id) => {
    console.log('Rejecting refund request with id:', id);
    // Implement reject functionality
  };

  const handleViewDetails = (id) => {
    console.log('Viewing details for refund request id:', id);
    // Implement view details functionality
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return 'success';
      case 'Rejected':
        return 'error';
      case 'Pending':
        return 'warning';
      case 'Processing':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
        Cancelled Booking Refund Request List
      </Typography>

      {/* Refund Request List Table */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Refund Request Data
        </Typography>
        
        <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 'bold' }}>
          Total Records: {refundRequestData.length}
        </Typography>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Booking ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Paid Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Deduction Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payable Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Payment Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Request By</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Request Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {refundRequestData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => (
                  <TableRow 
                    key={row.id}
                    sx={{ 
                      '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                        {row.bookingId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {formatCurrency(row.paidAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'error.main' }}>
                        {formatCurrency(row.deductionAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                        {formatCurrency(row.payableAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {row.paymentDetails}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {row.requestBy}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', maxWidth: '150px' }}>
                        {row.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.requestDate}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={row.status} 
                        size="small" 
                        color={getStatusColor(row.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewDetails(row.id)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Approve">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleApprove(row.id)}
                            disabled={row.status === 'Approved' || row.status === 'Rejected'}
                          >
                            <CheckIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleReject(row.id)}
                            disabled={row.status === 'Approved' || row.status === 'Rejected'}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={refundRequestData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 2 }}
        />
      </Paper>
    </Box>
  );
};

export default CancelledBookingRefundRequestList;